import { lockScroll } from '@theme/utilities';
import { removeTrapFocus } from '@theme/focus';
import { StandardEvents } from '@shopify/events';

/**
 * Valoraa: the cart drawer always opens as a modal overlay (Atolea's mini cart), at every width.
 *
 * Horizon's <theme-drawer> (assets/theme-drawer.js) opens non-modal from 990px up and squeezes the
 * page next to it. Its breakpoint is private, so instead of editing that core file we upgrade the
 * cart drawer's <dialog> to showModal() right after it opens (and after a breakpoint switch). That
 * gives the dimmed ::backdrop, the native focus trap and inert page at desktop too. The squeeze
 * margin is cancelled in CSS (snippets/vb-cart-styles.liquid) and the drawer has [no-persist], so
 * it is never restored as a sidebar after navigation. Closing still runs through ThemeDrawer
 * (Escape, backdrop click, close button), which unlocks the scroll it owns.
 */
const upgrade = () => {
  const drawer = document.getElementById('cart-drawer');
  const panel = drawer?.querySelector(':scope > dialog');
  if (!drawer || !(panel instanceof HTMLDialogElement)) return;
  if (!drawer.hasAttribute('open') || !panel.open) return;
  if (panel.matches(':modal')) return;

  const active = document.activeElement;
  removeTrapFocus();
  lockScroll(panel);
  panel.close();
  panel.showModal();

  const keep = active instanceof HTMLElement && panel.contains(active) ? active : null;
  const target = keep || panel.querySelector('.theme-drawer__close-button');
  if (target instanceof HTMLElement) target.focus({ preventScroll: true });
};

/**
 * Success state after an add (user request): a slim lime strip at the top of the drawer,
 * "✓ Added to your bag · <piece>, <option>", until the drawer closes. Only real cart data.
 * @param {any[]} lines @param {any} result
 */
const showAdded = async (lines, result, retried = false) => {
  const drawer = document.getElementById('cart-drawer');
  const panel = drawer?.querySelector(':scope > dialog');
  if (!(panel instanceof HTMLDialogElement)) return;
  const label = document.querySelector('script[data-vb-added]')?.getAttribute('data-vb-added') || '';
  const ids = (lines || []).map((line) => String(line?.merchandiseId ?? '').replace(/\D/g, ''));
  let items = result?.detail?.items || result?.cart?.lines || null;
  if (!Array.isArray(items) || !items.length || !items[0]?.variant_id) {
    try {
      items = (await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())).items;
    } catch {
      items = [];
    }
  }
  const added = items.filter((item) => ids.includes(String(item.variant_id)));
  const first = added[0] || items[0];
  // The add may still be settling (timeout path): look once more a moment later
  if (!added.length && !retried) {
    setTimeout(() => showAdded(lines, null, true), 900);
  }
  let strip = panel.querySelector(':scope > .vb-cart-added');
  if (!strip) {
    strip = document.createElement('p');
    strip.className = 'vb-cart-added';
    strip.setAttribute('role', 'status');
    panel.prepend(strip);
    panel.addEventListener('close', () => strip?.setAttribute('hidden', ''));
  }
  strip.replaceChildren();
  const tick = document.createElement('span');
  tick.className = 'vb-cart-added__tick';
  tick.setAttribute('aria-hidden', 'true');
  tick.textContent = '✓';
  const text = document.createElement('span');
  text.textContent = label;
  strip.append(tick, text);
  if (first) {
    const name = document.createElement('span');
    name.className = 'vb-cart-added__item';
    const more = added.length > 1 ? ` +${added.length - 1}` : '';
    const option = first.variant_title && first.variant_title !== 'Default Title' ? `, ${first.variant_title}` : '';
    name.textContent = `${first.product_title}${option}${more}`;
    strip.append(name);
  }
  strip.removeAttribute('hidden');
};

if (!(/** @type {any} */ (window).vbCartDrawerModal)) {
  /** @type {any} */ (window).vbCartDrawerModal = true;

  // ThemeDrawer dispatches this (bubbling) from the drawer right after show()/showModal()
  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') upgrade();
  });

  // ThemeDrawer switches to show() when the viewport crosses 990px; switch back after it runs
  window.matchMedia('(max-width: 989px)').addEventListener('change', () => setTimeout(upgrade, 0));

  // An older session may still have the drawer open as a restored sidebar
  customElements.whenDefined('theme-drawer').then(() => setTimeout(upgrade, 0));

  /*
   * Safety net for auto-open after ADD TO BAG. Horizon's <cart-drawer-component> opens the drawer on
   * the cart lines-update event ('add'); when the cart was empty it waits for event.promise, and a
   * component re-render or a close animation in flight can swallow that open. Once the add has
   * settled (event.promise, or 1500 ms, whichever comes first), open the drawer if it is still shut.
   * Skipped when the add came from inside another modal dialog (quick add): Horizon opens the
   * drawer after that dialog closes.
   */
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const e = /** @type {any} */ (event);
    if (e.action !== 'add') return;
    const source = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    if (source) return;

    const timeout = new Promise((resolve) => setTimeout(() => resolve({ detail: {} }), 1500));
    const settled = e.promise ? Promise.race([e.promise, timeout]) : timeout;
    settled
      .then((/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        // Give Horizon's own handler a frame to run first
        requestAnimationFrame(() => {
          const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
          const component = drawer?.querySelector('cart-drawer-component');
          if (!drawer || !component?.hasAttribute('auto-open')) return;
          if (document.querySelector('dialog:modal:not(#cart-drawer dialog)')) return;
          if (!drawer.hasAttribute('open') && typeof drawer.open === 'function') drawer.open();
          setTimeout(upgrade, 0);
          showAdded(e.lines, result);
        });
      })
      .catch(() => {});
  });
}
